import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Select, message } from 'antd';
import axiosInstance from '../../../axiosInstance';

const { Option } = Select;

const SubCategories = () => {
    const [subCategories, setSubCategories] = useState([]);
    const [categories, setCategories] = useState([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [editingSubCategory, setEditingSubCategory] = useState(null);

    useEffect(() => {
        fetchSubCategories();
        fetchCategories(); // Load categories for the dropdown
    }, []);

    const fetchSubCategories = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get('/sub-categories');
            if (response.data.subCategories) {
                setSubCategories(response.data.subCategories);
            } else {
                message.error('Failed to fetch sub-categories.');
                setSubCategories([]);
            }
        } catch (error) {
            console.error(error);
            message.error('Failed to fetch sub-categories.');
            setSubCategories([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await axiosInstance.get('/categories');
            if (response.data.categories) {
                setCategories(response.data.categories);
            } else {
                message.error('Failed to fetch categories.');
            }
        } catch (error) {
            console.error(error);
            message.error('Failed to fetch categories.');
        }
    };

    const showModal = (subCategory = null) => {
        if (subCategory) {
            setEditingSubCategory(subCategory);
            form.setFieldsValue({
                name: subCategory.name,
                category_id: subCategory.category.id,
            });
        } else {
            setEditingSubCategory(null);
            form.resetFields();
        }
        setIsModalVisible(true);
    };

    const handleOk = async (values) => {
        try {
            let response;
            if (editingSubCategory) {
                response = await axiosInstance.put(`/sub-categories/${editingSubCategory.id}`, values);
                message.success('Sub-category updated successfully');
            } else {
                response = await axiosInstance.post('/sub-categories', values);
                message.success('Sub-category added successfully');
            }
            fetchSubCategories();
            setIsModalVisible(false);
            form.resetFields();
        } catch (error) {
            message.error('Failed to save sub-category.');
        }
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        form.resetFields();
    };

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Category',
            dataIndex: ['category', 'name'],
            key: 'category',
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, subCategory) => (
                <Button onClick={() => showModal(subCategory)}>Edit</Button>
            ),
        },
    ];

    return (
        <div>
            <div style={{ textAlign: 'right', marginBottom: 16 }}>
                <Button type="primary" onClick={() => showModal()}>
                    Add Sub-Category
                </Button>
            </div>

            <Table
                columns={columns}
                dataSource={Array.isArray(subCategories) ? subCategories : []}
                rowKey="id"
                pagination={{ pageSize: 25 }}
                scroll={{ x: 'max-content' }}
                loading={loading}
            />

            <Modal
                title={editingSubCategory ? 'Edit Sub-Category' : 'Add New Sub-Category'}
                visible={isModalVisible}
                onOk={() => form.submit()}
                onCancel={handleCancel}
                okText={editingSubCategory ? 'Update' : 'Add'}
                cancelText="Cancel"
                destroyOnClose
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleOk}
                >
                    <Form.Item
                        name="name"
                        label="Name"
                        rules={[{ required: true, message: 'Please input the sub-category name!' }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="category_id"
                        label="Category"
                        rules={[{ required: true, message: 'Please select a category!' }]}
                    >
                        <Select placeholder="Select a category">
                            {categories.map((category) => (
                                <Option key={category.id} value={category.id}>
                                    {category.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default SubCategories;
